import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  getIsValidStationLocation,
  IPassengerStationLocationResponseDto,
} from "@ryde/shared";

import {
  MapLibreMapHost,
  MapLibreWalkingRoute,
  useMapLibreLocationsAutoFit,
} from "../../../common/map/maplibre";
import { PASSENGER_ADDRESS_STATION_PINS } from "./passenger-address.constants";
import { PassengerAddressStationPin } from "./passenger-address.station-pin";
import { StyledPassengerAddress } from "./passenger-address.styled";
import { usePassengerAddress } from "./use-passenger-address";

/**
 * The passenger's source and destination stations on a map, with the walk between the two source stations
 * drawn between them. Read-only — the stations themselves are chosen in the form above it.
 */
export const PassengerAddressBlock: FC = () => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // One popup at a time, as Leaflet enforced per map. Held here rather than in each pin so opening one
  // closes the others; clicking the map closes it through the popup's own closeOnClick.
  const [openPinKey, setOpenPinKey] = useState<
    keyof IPassengerStationLocationResponseDto | null
  >(null);
  const closePopup = useEvent(() => setOpenPinKey(null));

  const { locations, distanceAndTime, walkPositions, activeLocations } =
    usePassengerAddress();

  useMapLibreLocationsAutoFit({
    mapRef,
    locations: activeLocations,
    isMapLoaded,
  });

  const pins = useMemo(
    () =>
      PASSENGER_ADDRESS_STATION_PINS.flatMap(pin => {
        const location = locations?.[pin.locationKey] ?? undefined;

        return getIsValidStationLocation(location)
          ? [{ ...pin, location }]
          : [];
      }),
    [locations],
  );

  return (
    <StyledPassengerAddress>
      <MapLibreMapHost
        mapRef={mapRef}
        isFullscreenEnabled
        onLoadedChange={setIsMapLoaded}
      >
        {isMapLoaded &&
          pins.map(pin => (
            <PassengerAddressStationPin
              key={pin.locationKey}
              {...pin}
              isPopupOpen={openPinKey === pin.locationKey}
              onOpen={() => setOpenPinKey(pin.locationKey)}
              onClose={closePopup}
            />
          ))}

        {isMapLoaded && distanceAndTime && (
          <MapLibreWalkingRoute
            id="passenger-address-walk"
            positions={walkPositions}
            distanceAndTime={distanceAndTime}
          />
        )}
      </MapLibreMapHost>
    </StyledPassengerAddress>
  );
};
